import { components } from "@appretio/api";
import { DialogFeedbackDescriptor } from "@appretio/ui-kit";
import { createContext, useContext, useMemo } from "react";
import type { GridViewRestore } from "./gridViewRestore";

/**
 * The report kinds the modal offers. Aliased straight off the generated
 * OpenAPI schema (docs/code-guidelines/dependencies.md — "Use generated
 * OpenAPI types — don't redeclare them"), so adding a category server-side
 * cannot leave the frontend's union silently behind.
 */
export type FeedbackCategory = components["schemas"]["FeedbackCategory"];

/**
 * The cell a "Report this value" entry points at — attached verbatim under
 * `context.cell` so a data-trust report carries exactly what the user saw.
 */
export interface FeedbackCellContext {
  /** Grid column id / KPI field name (e.g. `ns3`). */
  field: string;
  /** Column header as displayed to the user. */
  header?: string;
  /** The value as displayed in the cell. */
  value?: unknown;
  /** Style option identifiers, when the row carries them. */
  style_option?: string;
  style_option_number?: number | string;
  /** Location identifiers, when the row carries them. */
  stock_location?: string;
  sales_location?: string;
  /** Compact snapshot of the row's scalar fields. */
  row?: Record<string, unknown>;
  /**
   * Cells that were in edit mode when the report was opened (open editors
   * at right-click time — submit-time edit state rides the page context).
   */
  editing_cells?: { row_index: number | null; col_id: string }[];
  /**
   * When the user had a multi-cell range selected: every selected cell
   * (capped at MAX_RANGE_CELLS), each with its own row identity. `cell`
   * itself stays the clicked cell.
   */
  cells?: {
    row_index: number;
    field: string;
    header?: string;
    value?: unknown;
    [key: string]: unknown;
  }[];
  /** The real selected-cell count, in case `cells` was capped. */
  cells_total?: number;
  /** Identity keys a grid's `describeRow` extracts (domain-specific). */
  [key: string]: unknown;
}

/** Values a caller can pre-fill when opening the modal programmatically. */
export interface FeedbackPrefill {
  /** Pre-selected category (e.g. `data_wrong` for "Report this value"). */
  category?: FeedbackCategory;
  /** The reported cell, attached under `context.cell`. */
  cell?: FeedbackCellContext;
}

/**
 * A page-scoped context getter: returns the extra `context` keys the current
 * page wants attached to reports written while it is mounted (e.g. Tradex's
 * applied brand/filter/window selection).
 */
export type FeedbackPageContextGetter = () => Record<string, unknown>;

/**
 * One dialog that is open right now. Registered by the trigger ui-kit's
 * Dialog renders in its title row, so the registration lasts exactly as
 * long as the dialog does.
 */
export interface FeedbackOpenDialog extends DialogFeedbackDescriptor {
  /** When the dialog opened, stamped once on mount. */
  opened_at: string;
}

/**
 * The modal's open/prefill state. Split from {@link FeedbackApi} so that
 * open/close — which change on every modal toggle — only re-render the
 * components that actually display the modal, never the (potentially heavy)
 * pages that merely register context getters.
 */
export interface FeedbackState {
  /** Whether the feedback modal is open. */
  isOpen: boolean;
  /** The prefill the modal was opened with, if any. */
  prefill: FeedbackPrefill | null;
}

/**
 * The stable feedback API: every function keeps its identity for the
 * provider's lifetime, so consumers (and their effects) never churn when
 * the modal opens or closes.
 */
export interface FeedbackApi {
  /** Open the feedback modal, optionally pre-filled. */
  open: (prefill?: FeedbackPrefill) => void;
  /** Close the feedback modal (drops any prefill). */
  close: () => void;
  /**
   * Register a page-context getter; returns the unregister function.
   * Prefer the `useRegisterFeedbackContext` hook, which ties the
   * registration to the component's lifetime.
   */
  registerPageContext: (getter: FeedbackPageContextGetter) => () => void;
  /**
   * Register a dialog as open; returns the unregister function. Reports
   * written while it is registered carry it under `context.modal`, the
   * last registration winning as the topmost dialog.
   */
  registerOpenDialog: (dialog: FeedbackOpenDialog) => () => void;
  /**
   * Merge every registered getter's keys. Top-level keys whose values are
   * plain objects merge one level deep (so the shared selector and the page
   * can both contribute to e.g. `top_bar`); anything else, later
   * registrations win.
   */
  collectPageContext: () => Record<string, unknown>;
  /** The keyed grids (`DataTable`'s `feedbackViewKey`). */
  gridViews: GridViewRestore;
}

export type FeedbackContextValue = FeedbackState & FeedbackApi;

export const FeedbackStateContext = createContext<FeedbackState | null>(null);
export const FeedbackApiContext = createContext<FeedbackApi | null>(null);

/**
 * The stable feedback API (open/close/register/collect) without the modal
 * state — consuming it never re-renders on modal open/close. Null outside a
 * FeedbackProvider so shared building blocks can degrade to a no-op.
 */
export function useFeedbackApi(): FeedbackApi | null {
  return useContext(FeedbackApiContext);
}

/**
 * Access the feedback modal state plus API. Must be used within a
 * FeedbackProvider. Note this re-renders the caller on every modal
 * open/close — components that only need `open` (or to register a getter)
 * should prefer `useFeedbackApi` / `useRegisterFeedbackContext`.
 */
export function useFeedback(): FeedbackContextValue {
  const state = useContext(FeedbackStateContext);
  const api = useContext(FeedbackApiContext);
  const value = useMemo(
    () => (state && api ? { ...state, ...api } : null),
    [state, api]
  );
  if (!value) {
    throw new Error("useFeedback must be used within a FeedbackProvider");
  }
  return value;
}
